import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createMonitor } from '../services/api';
import Tooltip from '../components/Tooltip';
import ErrorModal from '../components/ErrorModal';
import CustomSelect from '../components/CustomSelect';

const CreateMonitor = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    type: 'ssl_check',
    url: '',
    method: 'GET',
    intervalMinutes: 180,
    timeoutMs: 8000,
    enabled: true,
    env: 'prod',
    consecutiveFailThreshold: 2,
    notifyEmails: [],
    expectedStatusCodes: [200, 201, 202, 204],
    bodyMatchPattern: '',
    heartbeatKey: '',
    heartbeatWindowMinutes: 60,
  });
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddEmail = () => {
    if (emailInput && !formData.notifyEmails.includes(emailInput)) {
      setFormData((prev) => ({
        ...prev,
        notifyEmails: [...prev.notifyEmails, emailInput],
      }));
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (email) => {
    setFormData((prev) => ({
      ...prev,
      notifyEmails: prev.notifyEmails.filter((e) => e !== email),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean up data based on type
      const submitData = { ...formData };
      
      if (formData.type === 'http_status') {
        delete submitData.bodyMatchPattern;
        delete submitData.heartbeatKey;
        delete submitData.heartbeatWindowMinutes;
      } else if (formData.type === 'http_body') {
        delete submitData.expectedStatusCodes;
        delete submitData.heartbeatKey;
        delete submitData.heartbeatWindowMinutes;
      } else if (formData.type === 'ssl_check') {
        delete submitData.expectedStatusCodes;
        delete submitData.bodyMatchPattern;
        delete submitData.heartbeatKey;
        delete submitData.heartbeatWindowMinutes;
        delete submitData.method;
      } else if (formData.type === 'heartbeat') {
        delete submitData.expectedStatusCodes;
        delete submitData.bodyMatchPattern;
        delete submitData.method;
        delete submitData.url;
      }

      // Convert expectedStatusCodes string to array if needed
      if (submitData.expectedStatusCodes && typeof submitData.expectedStatusCodes === 'string') {
        submitData.expectedStatusCodes = submitData.expectedStatusCodes
          .split(',')
          .map((s) => parseInt(s.trim()))
          .filter((n) => !isNaN(n));
      }

      const response = await createMonitor(submitData);
      navigate(`/monitor/${response.data._id}`);
    } catch (error) {
      console.error('Error creating monitor:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-2">
          <Link to="/" className="text-blue-600 hover:text-blue-700 mr-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white">Create New Monitor</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mt-2">Set up a new monitoring endpoint to track uptime and performance</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Basic Information Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Basic Information
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              Monitor Name <span className="text-red-500 ml-1">*</span>
              <Tooltip text="A friendly name to identify this monitor.\n\nExample: 'Production API', 'Main Website', 'Payment Service'">
                <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="e.g., Production API"
              className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              Monitor Type <span className="text-red-500 ml-1">*</span>
              <Tooltip text="Choose what to monitor:\n\nSSL Check: Verify site is reachable and SSL is valid\nHTTP Status: Check if response code is 200-299\nHTTP Body: Check if response contains specific text\nHeartbeat: Monitor cron jobs via POST endpoint">
                <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tooltip text="Checks if site is reachable and SSL certificate is valid.\n\nExample: https://example.com is up and SSL valid = OK\nExample: SSL expired = Alert">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'ssl_check' }))}
                  className={`p-4 border-2 rounded-lg transition-all w-full ${
                    formData.type === 'ssl_check'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white mb-1">SSL Check</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Connectivity & SSL</div>
                  </div>
                </button>
              </Tooltip>
              <Tooltip text="Checks if HTTP response status code matches expected values.\n\nExample: Monitor returns 200 = OK\nExample: Monitor returns 500 = Alert">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'http_status' }))}
                  className={`p-4 border-2 rounded-lg transition-all w-full ${
                    formData.type === 'http_status'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white mb-1">HTTP Status</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Check status codes</div>
                  </div>
                </button>
              </Tooltip>
              <Tooltip text="Checks if the response body contains specific text.\n\nExample: Body contains 'success' = OK\nExample: Body contains 'error' = Alert">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'http_body' }))}
                  className={`p-4 border-2 rounded-lg transition-all w-full ${
                    formData.type === 'http_body'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white mb-1">HTTP Body</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Check content</div>
                  </div>
                </button>
              </Tooltip>
              <Tooltip text="Monitors cron jobs that POST to /heartbeat/:key endpoint.\n\nExample: Cron job posts every hour = OK\nExample: No heartbeat for 2 hours = Alert">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'heartbeat' }))}
                  className={`p-4 border-2 rounded-lg transition-all w-full ${
                    formData.type === 'heartbeat'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-md'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                >
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white mb-1">Heartbeat</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">Cron monitoring</div>
                  </div>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Monitor Configuration Section */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-700 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
            <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Monitor Configuration
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {formData.type !== 'heartbeat' && formData.type !== 'ssl_check' && (
            <>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  URL <span className="text-red-500 ml-1">*</span>
                  <Tooltip text="The full URL to monitor.\n\nExample: 'https://api.example.com/health'\nExample: 'https://www.example.com'">
                    <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                  </div>
                  <input
                    type="url"
                    name="url"
                    value={formData.url}
                    onChange={handleChange}
                    required={formData.type !== 'heartbeat'}
                    placeholder="https://api.example.com/health"
                    className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  HTTP Method
                  <Tooltip text="The HTTP method to use for the request.\n\nGET: Retrieve data (most common)\nPOST: Send data to server\nPUT: Update resource\nDELETE: Remove resource\n\nExample: Use GET for health checks">
                    <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </Tooltip>
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {['GET', 'POST', 'PUT', 'DELETE'].map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, method }))}
                      className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all ${
                        formData.method === method
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 shadow-md'
                          : 'border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                      }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {formData.type === 'ssl_check' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                URL <span className="text-red-500 ml-1">*</span>
                <Tooltip text="HTTPS URL to check connectivity and SSL certificate.\n\nChecks:\n- Site is reachable\n- SSL certificate is valid\n- Certificate expiration date\n\nExample: 'https://example.com'\nExample: 'https://api.example.com'">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  type="url"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  required
                  placeholder="https://example.com"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg">
                <p className="text-xs text-blue-800 dark:text-blue-300 flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Checks if the site is reachable and validates SSL certificate (expiry, validity). Use HTTPS URLs.
                </p>
              </div>
            </div>
          )}

          {formData.type === 'heartbeat' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Heartbeat Key <span className="text-red-500 ml-1">*</span>
                <Tooltip text="Unique key for your cron job to POST to.\n\nYour cron job should POST to:\n/heartbeat/YOUR_KEY\n\nExample key: 'backup-job-123'\nExample cron: curl -X POST http://localhost:3001/heartbeat/backup-job-123">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <input
                  type="text"
                  name="heartbeatKey"
                  value={formData.heartbeatKey}
                  onChange={handleChange}
                  required
                  placeholder="unique-heartbeat-key"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800 flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Use this key when POSTing to <code className="bg-blue-100 px-1 rounded">/heartbeat/:key</code> endpoint from your cron job
                </p>
              </div>
            </div>
          )}

          {formData.type === 'http_status' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Expected Status Codes
                <Tooltip text="HTTP status codes that indicate success.\n\nCommon codes:\n200: OK\n201: Created\n202: Accepted\n204: No Content\n\nExample: '200, 201, 202'\nExample: '200'">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <input
                type="text"
                name="expectedStatusCodes"
                value={formData.expectedStatusCodes.join(', ')}
                onChange={(e) => {
                  const codes = e.target.value.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n));
                  setFormData((prev) => ({ ...prev, expectedStatusCodes: codes.length > 0 ? codes : [200] }));
                }}
                placeholder="200, 201, 202, 204"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Comma-separated list of HTTP status codes that indicate success</p>
            </div>
          )}

          {formData.type === 'http_body' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Body Match Pattern
                <Tooltip text="Text that must be present in the response body.\n\nThe check passes only if this text is found.\n\nExample: 'success'\nExample: 'status: ok'\nExample: 'healthy'">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <input
                type="text"
                name="bodyMatchPattern"
                value={formData.bodyMatchPattern}
                onChange={handleChange}
                placeholder="Text that must be present in response body"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">The response body must contain this text for the check to pass</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Check Interval (minutes) <span className="text-red-500 ml-1">*</span>
                <Tooltip text="How often to check this monitor.\n\nCommon intervals:\n5 min: Critical services\n15 min: Important services\n60 min: Standard monitoring\n180 min (3 hrs): Low priority\n\nExample: 15 (checks every 15 minutes)">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="intervalMinutes"
                  value={formData.intervalMinutes}
                  onChange={handleChange}
                  required
                  min="1"
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-sm">min</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Timeout (ms)
                <Tooltip text="Maximum time to wait for a response before timing out.\n\nRecommended:\n5000ms (5s): Fast APIs\n8000ms (8s): Standard\n10000ms (10s): Slow services\n\nExample: 8000 (8 seconds)">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="timeoutMs"
                  value={formData.timeoutMs}
                  onChange={handleChange}
                  min="1000"
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-sm">ms</span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Fail Threshold
                <Tooltip text="Number of consecutive failures before sending an alert.\n\nThis prevents false alarms from temporary network issues.\n\nExample: 2 (alert after 2 failures in a row)\nExample: 3 (alert after 3 failures)">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <input
                type="number"
                name="consecutiveFailThreshold"
                value={formData.consecutiveFailThreshold}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Number of consecutive failures before alerting</p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Environment
                <Tooltip text="The environment this monitor is for.\n\nProduction: Live/production services\nStaging: Test/staging environment\n\nExample: Production (for live services)\nExample: Staging (for testing)">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <CustomSelect
                name="env"
                value={formData.env}
                onChange={handleChange}
                options={[
                  { value: 'prod', label: 'Production' },
                  { value: 'stage', label: 'Staging' },
                ]}
              />
            </div>
          </div>

          {formData.type === 'heartbeat' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                Heartbeat Window (minutes)
                <Tooltip text="Maximum time allowed between heartbeats.\n\nIf no heartbeat is received within this window, the monitor will alert.\n\nExample: 60 (alert if no heartbeat for 60 minutes)\nExample: 120 (alert if no heartbeat for 2 hours)">
                  <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </Tooltip>
              </label>
              <div className="relative">
                <input
                  type="number"
                  name="heartbeatWindowMinutes"
                  value={formData.heartbeatWindowMinutes}
                  onChange={handleChange}
                  min="1"
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <span className="text-gray-400 text-sm">min</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Maximum time between heartbeats before alerting</p>
            </div>
          )}
        </div>

        {/* Notifications Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-gray-700 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
            <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Notifications & Settings
          </h2>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
              Notification Emails
              <Tooltip text="Email addresses to notify when monitor goes down or recovers.\n\nYou'll receive:\n- Alert when monitor fails\n- Recovery notification when it's back up\n\nExample: admin@example.com\nExample: team@example.com, oncall@example.com">
                <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </label>
            <div className="flex space-x-2 mb-3">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEmail())}
                  placeholder="email@example.com"
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
              <button
                type="button"
                onClick={handleAddEmail}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 shadow-md hover:shadow-lg transition-all flex items-center"
              >
                <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add
              </button>
            </div>
            {formData.notifyEmails.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                {formData.notifyEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center px-3 py-1.5 bg-blue-100 text-blue-800 rounded-lg text-sm font-medium border border-blue-200"
                  >
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                    {email}
                    <button
                      type="button"
                      onClick={() => handleRemoveEmail(email)}
                      className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
            <input
              type="checkbox"
              name="enabled"
              id="enabled"
              checked={formData.enabled}
              onChange={handleChange}
              className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor="enabled" className="ml-3 text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer flex items-center">
              Enable monitor immediately
              <Tooltip text="Start monitoring immediately after creation.\n\nIf unchecked, the monitor will be created but won't run checks until you enable it manually.\n\nExample: Checked = starts monitoring right away\nExample: Unchecked = create but don't monitor yet">
                <svg className="w-4 h-4 ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </Tooltip>
            </label>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-6 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center"
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Create Monitor
              </>
            )}
          </button>
        </div>
      </form>

      <ErrorModal
        isOpen={error !== null}
        onClose={() => setError(null)}
        title="Failed to Create Monitor"
        message={error || ''}
      />
    </div>
  );
};

export default CreateMonitor;
